import { useState } from "react";
import type { FormEvent } from "react";

interface PaginationProps {
  page: number;
  totalPages: number;
  onChange: (page: number) => void;
}

export default function Pagination({ page, totalPages, onChange }: PaginationProps) {
  const [jumpValue, setJumpValue] = useState("");

  if (totalPages <= 1) return null;

  const pages = Array.from({ length: totalPages }, (_, index) => index + 1).filter((item) => {
    if (totalPages <= 7) return true;
    return item === 1 || item === totalPages || Math.abs(item - page) <= 1;
  });

  const handleJump = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const parsed = Number(jumpValue);
    if (!Number.isNaN(parsed) && parsed >= 1 && parsed <= totalPages) {
      onChange(parsed);
      setJumpValue("");
    }
  };

  return (
    <div className="mt-8 flex flex-col items-center gap-4 border-t border-background-200 pt-6 sm:flex-row sm:justify-between">
      <p className="text-[12px] text-foreground-500">
        第 <span className="font-medium text-foreground-900">{page}</span> / {totalPages} 页
      </p>
      <div className="flex flex-wrap items-center justify-center gap-1.5">
        <button
          type="button"
          onClick={() => onChange(Math.max(1, page - 1))}
          disabled={page === 1}
          className="flex h-8 items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-2.5 text-[12px] text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-700 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <i className="ri-arrow-left-s-line text-sm" />
          上一页
        </button>
        {pages.map((item, index) => {
          const previous = pages[index - 1];
          const showGap = previous !== undefined && item - previous > 1;
          return (
            <span key={item} className="flex items-center gap-1.5">
              {showGap && <span className="px-1 text-[12px] text-foreground-400">…</span>}
              <button
                type="button"
                onClick={() => onChange(item)}
                aria-current={item === page ? "page" : undefined}
                className={`h-8 w-8 rounded-md text-[12px] transition-colors ${
                  item === page
                    ? "bg-primary-500 font-semibold text-background-50"
                    : "border border-background-300 text-foreground-600 hover:border-primary-300 hover:text-primary-700"
                }`}
              >
                {item}
              </button>
            </span>
          );
        })}
        <button
          type="button"
          onClick={() => onChange(Math.min(totalPages, page + 1))}
          disabled={page === totalPages}
          className="flex h-8 items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-2.5 text-[12px] text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-700 disabled:cursor-not-allowed disabled:opacity-40"
        >
          下一页
          <i className="ri-arrow-right-s-line text-sm" />
        </button>
      </div>
      <form onSubmit={handleJump} className="flex items-center gap-2">
        <label htmlFor="jump-page" className="whitespace-nowrap text-[12px] text-foreground-500">
          跳至
        </label>
        <input
          id="jump-page"
          type="number"
          min={1}
          max={totalPages}
          value={jumpValue}
          onChange={(event) => setJumpValue(event.target.value)}
          className="h-8 w-14 rounded-md border border-background-300 bg-background-50 px-2 text-center text-[12px] text-foreground-900 outline-none focus:border-primary-400"
        />
        <button
          type="submit"
          className="h-8 whitespace-nowrap rounded-md bg-secondary-500 px-2.5 text-[12px] font-medium text-background-50 transition-colors hover:bg-secondary-600"
        >
          确定
        </button>
      </form>
    </div>
  );
}